/****************************************************
 * Module Name:  SellToOutsource.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Box, Button, Container, Tooltip, Typography, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGridPro, GridEditInputCell, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonStoneSelection from "../common/commonStoneSelection";
import Utility from "../../utility/utility";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import Constants from "../../utility/constants";
import StoneDetailModal from "../common/stoneDetailModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import CommonSnackbar from "../common/commonSnackBar";
import SellToOutsourceModal from "./sellToOutsourceModal";
import CustomerSellOutsourceProxy from "../../utility/proxy/manager/customersellOutsourceProxy";
import NoPermission from "../common/noPermission";
import EditIcon from '@mui/icons-material/Edit';
import { BackupTable } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Papa from 'papaparse';
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_REQUESTED": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

export default function SellToOutsource() {
    const [stoneData, setStoneData] = useState([]);
    const [stoneLoader, setStoneLoader] = useState(false);
    const [fetchData, setFetchData] = useState(true);
    const [selectionModel, setSelectionModel] = useState([]);
    const [kapanId, setKapanId] = useState("");
    const [stoneId, setStoneId] = useState("");
    const [packetId, setPacketId] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [discountSet, setDiscountSet] = useState({});
    const [ppcDiscountSet, setppcDiscountSet] = useState({});
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [showOutsourceModal, setShowOutsourceModal] = useState(false);
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);
    const [local_Permission,] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.OPERATION_SELL_TO_OUTSOURCE
        )
    );
    const [uploadLoader, setUploadLoader] = useState(false);
    const [uploadSuccessSnackbar, setUploadSuccessSnackbar] = useState(false);
    const [data, setData] = useState([]);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

    //top most summary table 
    const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
    const [selectedTotalSize, setSelectedTotalSize] = useState("0");
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
    const [selectedavgPrice, setSelectedavgPrice] = useState("0");
    const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");

    useEffect(() => {
        PageTitle("OPERATION_SELL_TO_OUTSOURCE");
    })

    const loadTotalData = (data) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size

        data.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

        });
        setNoOfSelectedStones(data.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    };

    useEffect(() => {
        if (selectionModel.length <= 0) {
            loadTotalData(stoneData);
            return;
        }
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        stoneData.forEach((iData) => {
            if (selectionModel.includes(iData.stoneid)) {
                let pricePerCarat = iData.price
                totalSize += iData.size;
                totalPrice += (iData.size * pricePerCarat);
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
            }
        });
        setNoOfSelectedStones(selectionModel.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );

    }, [selectionModel]);

    const handleSearch = () => {
        setSelectionModel([]);
        setDiscountSet({});
        setppcDiscountSet({});
        setStoneLoader(true);
        let data = {
            "status": ["AVAILABLE", "MEMO-REQUESTED"],
        };
        StoneProxy.GetFilteredStock(data, (responseData) => {
            //Success
            if (responseData) {
                const distinctStatus = Utility.getDistinctOptions(responseData, 'status');
                prepareColumns(distinctStatus);
                setStoneData(responseData);
                loadTotalData(responseData);
                setStoneLoader(false);
                setFetchData(false);
            } else {
                // Failure
                setStoneLoader(false);
                setFetchData(false);
                setErrorSnackbar(true);
            }
        });
    };

    const prepareColumns = (distinctStatus) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "status") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = distinctStatus;
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);
                if (columnData.field === "flo") {
                    columnNames.push(
                        {
                            field: "editeddiscount",
                            headerName: "EDITED DIS",
                            headerAlign: "center",
                            align: "right",
                            minWidth: 50,
                            type: "number",
                            valueFormatter: (params) => params.value,
                            editable: (params) => {
                                return Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE";
                            },
                            renderCell: (params) => (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ paddingRight: '18px !important' }}>{params.value}</span>
                                    {(Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE") ? <EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} /> : null}
                                </div>
                            ),
                            renderEditCell: (params) => {
                                return (
                                    Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE" ?
                                        <Box onChange={(e) => {
                                            setDiscountSet(prevDiscountSet => ({
                                                ...prevDiscountSet,
                                                [params.row.stoneid]: e.target.value,
                                            }));
                                        }}><GridEditInputCell {...params} /></Box>
                                        :
                                        null
                                )
                            },
                            cellClassName: (params) => {
                                return (Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE") ? 'lavenderCell' : 'cursorNA';
                            }
                        }
                    );
                    columnNames.push({
                        field: "ppcdiscount",
                        headerName: "EDITED PPC",
                        headerAlign: "center",
                        align: "right",
                        minWidth: 50,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        editable: (params) => {
                            return Utility.isAddAllowed(local_Permission) &&
                                params.row.colortype === "FANCY";
                        },
                        renderCell: (params) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ paddingRight: '18px !important' }}></span>
                                {(Utility.isAddAllowed(local_Permission) && params.row.colortype === "FANCY") ? <EditIcon style={{ fontSize: '1rem', marginLeft: 10, marginBottom: 3, }} /> :
                                    <div style={{ cursor: 'not-allowed' }} />}
                            </div>
                        ),
                        renderEditCell: (params) => {
                            return (
                                Utility.isAddAllowed(local_Permission) &&
                                    params.row.colortype === "FANCY" ?
                                    <Box onChange={(e) => {
                                        setppcDiscountSet(prevppcDiscountSet => ({
                                            ...prevppcDiscountSet,
                                            [params.row.stoneid]: e.target.value,
                                        }));
                                    }}><GridEditInputCell {...params} /></Box> :
                                    null
                            )
                        },
                        cellClassName: (params) => {
                            return (Utility.isAddAllowed(local_Permission) && params.row.colortype === "FANCY") ? 'lavenderCell' : 'cursorNA';
                        }
                    }
                    );
                }
            }
        });
        setColumnNames(columnNames);
    }

    useEffect(() => {
        if (fetchData) {
            handleSearch();
        }
    }, [fetchData]);

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, stoneData, selectionModel);

        setStoneData(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const handleUpload = (file) => {
        const reader = new FileReader();
        setUploadLoader(true);
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    let ids = []
                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['stoneid', 'discount/ppc'];
                    let matchingColumns = headersFromCSV.filter(field => requiredColumns.includes(field));
                    if (matchingColumns.length === requiredColumns.length) {
                        let updatedStoneData = [...stoneData];
                        result.data.forEach(record => {
                            updatedStoneData.forEach(row => {
                                if (row.stoneid === parseInt(record.stoneid)) {
                                    if (row.colortype === "WHITE") {
                                        row.editeddiscount = parseInt(record['discount/ppc']) || row.editeddiscount;
                                        setDiscountSet(prevDiscountSet => ({
                                            ...prevDiscountSet,
                                            [row.stoneid]: record['discount/ppc']
                                        }));
                                    }
                                    else {
                                        row.ppcdiscount = parseInt(record['discount/ppc']) || row.ppcdiscount;
                                        setppcDiscountSet(prevDiscountSet => ({
                                            ...prevDiscountSet,
                                            [row.stoneid]: record['discount/ppc']
                                        }));
                                    }
                                    ids = result.data.map(record => parseInt(record.stoneid));
                                    setSelectionModel(ids);
                                }
                            });
                        });
                        setData(ids);
                        setStoneData(updatedStoneData);
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setData([]);
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setData([]);
                    setUploadLoader(false);
                }
            });
        };

        reader.readAsText(file);
    };


    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handleClose = () => {
        setShowStoneDetailModal(false);
        setShowPacketDetailModal(false);
        setShowSuccessSnackbar(false);
    };

    const handlePacketDetailModal = (params, kapanid) => {
        setPacketId(params);
        setKapanId(kapanid);
        setShowPacketDetailModal(true);
    };

    const SellToOutsource = (data) => {
        setLoadingButton(true);
        CustomerSellOutsourceProxy.createCustomerSellOutsource(data,
            (res) => {
                if (res) {
                    //Success
                    setShowSuccessSnackbar(true);
                    setSuccessMessage("Sell to Outsource Successful");
                    setSnackbarVariant("success");
                    setShowOutsourceModal(false);
                    setLoadingButton(false);
                } else {
                    //Failure
                    setShowSuccessSnackbar(true);
                    setSuccessMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                    setLoadingButton(false);
                }
                setDiscountSet({});
                setppcDiscountSet({});
                handleSearch();
            })
    }

    const handleValidation = (customer) => {
        //TODO do validation if needed here
        return true;
    };

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: (params) => {
                if (params.value === null || params.row.kapanid === null) {
                    return;
                } else {
                    return Utility.NeedToShowPacketDetailModal() ? (
                        <Button
                            sx={{ textDecorationLine: "underline", color: "black" }}
                            onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
                        >
                            {params.value}
                        </Button>
                    ) : (
                        <div>{params.value}</div>
                    );
                }
            }
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 40,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 75,
            renderCell: renderCellExpand,
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 65,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 50,
            renderCell: renderCellExpand,
        },
        "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 190,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 60,
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 100,
            renderCell: renderCellExpand,
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 40,
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
            renderCell: renderCellExpand,
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: renderCellExpand,
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 100,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },

    };

    const allowed = Utility.isReadAllowed(local_Permission);

    const rows = stoneData;
    const columns = columnNames;

    function CustomToolbar() {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <CommonStoneSelection
                    title={"Enter multiple Stone Ids"}
                    handleSelection={handleSelection}
                    buttonDisabled={rows.length === 0}
                />

                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                {stoneLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => handleSearch()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}

                {Utility.isAddAllowed(local_Permission) ? (
                    <Tooltip title="Sell Stones to Outsource">
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            sx={{ ml: "5px" }}
                            startIcon={<DiamondOutlinedIcon size="large" />}
                            color="success"
                            disabled={selectionModel.length === 0 || stoneLoader}
                            onClick={() => setShowOutsourceModal(true)}
                        >
                            Customer Sell To Outsource
                        </Button>
                    </Tooltip>
                ) : null}
                <Tooltip title="Download Sample Sell Outsource Upload">
                    <a
                        href="/demo_excel/SampleSellOutsourceUpload.csv"
                        download="SampleSellOutsourceUpload.csv"
                        style={{ color: "red", marginRight: "10px", marginLeft: "10px" }}
                    >
                        <BackupTable size={25} />
                    </a>
                </Tooltip>
                <Tooltip title="Upload Sell Outsource">
                    <LoadingButton
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ mr: "10px" }}
                        component="label"
                        loading={uploadLoader}
                        loadingPosition="start"
                        startIcon={<UploadFileIcon size="large" />}
                    >
                        Upload
                        <input
                            id="file-input"
                            hidden
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            multiple
                            type="file"
                            onChange={(e) => handleUpload(e.target.files[0])}
                            onClick={(e) => handleClear()}
                        />
                    </LoadingButton>
                </Tooltip>
                {showOutsourceModal ? (
                    <SellToOutsourceModal
                        show={true}
                        loading={loadingButton}
                        stoneData={stoneData}
                        stoneId={selectionModel}
                        discount={discountSet}
                        ppcDiscount={ppcDiscountSet}
                        handleSave={SellToOutsource}
                        setShow={setShowOutsourceModal}
                        handleValidation={handleValidation}
                    />
                ) : null}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }
    return (
        !allowed ? <NoPermission /> :
            <Container maxWidth="xxl">
                <CommonSnackbar
                    show={errorSnackbar}
                    message={Constants.APiFailErrorMessage}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={"Failed"}
                    handleClose={() => setErrorSnackbar(false)}
                />
                {showKapanDetailModal ? (
                    <KapanHistoryModal
                        show={showKapanDetailModal}
                        kapanId={kapanId}
                        close={() => setShowKapanDetailModal(false)}
                    />
                ) : null}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexGrow: 1,
                        flexBasis: 0,
                        overflow: "hidden",
                        color: "black",
                        fontWeight: "normal",
                        px: 1,
                        textAlign: "center",
                        paddingTop: "21px",
                        paddingBottom: "21px",
                        paddingLeft: "8px",
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            No of Stones: <b>{noOfSelectedStones}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Total Weight: <b>{selectedTotalSize}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Total Price: <b>{selectedtotalPrice}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Avg Price: <b>{selectedavgPrice}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Avg Discount: <b>{selectedAvgDiscount}</b>
                        </Typography>
                    </Stack>
                </Box>
                {data ? (
                    <CommonSnackbar
                        show={uploadSuccessSnackbar}
                        message={
                            data.length !== 0
                                ? `File loaded Successfully`
                                : `The headers in the CSV file do not match the expected headers or Stone ids do not match `
                        }
                        variant={data.length !== 0 ? "success" : "error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={data.length !== 0 ? "Success" : "Error"}
                        handleClose={handleClose}
                    />
                ) : null}

                {showSuccessSnackbar ? (
                    <CommonSnackbar
                        show={true}
                        message={successMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" : "Failed"}
                        handleClose={handleClose}
                    />
                ) : null}
                {showStoneDetailModal ? (
                    <StoneDetailModal
                        show={showStoneDetailModal}
                        stoneId={stoneId}
                        handleClose={handleClose}
                    />
                ) : null}
                {showPacketDetailModal ? (
                    <PacketHistoryModal
                        show={showPacketDetailModal}
                        packetId={packetId}
                        kapanId={kapanId}
                        close={handleClose}
                    />
                ) : null}

                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        "& .cursorNA": {
                            cursor: "not-allowed"
                        },
                        height: '80vh'
                    }}
                >
                    <StyledDataGrid
                        disableRowSelectionOnClick={true}
                        localeText={{ noRowsLabel: "No Record Found" }}
                        sx={{ height: "80vh" }}
                        slots={{
                            toolbar: CustomToolbar,
                        }}
                        rows={rows}
                        density="compact"
                        columns={columns}
                        getRowId={(row) => row.stoneid}
                        checkboxSelection={true}
                        onRowSelectionModelChange={(newSelectionModel) => {
                            setSelectionModel(newSelectionModel);
                        }}
                        loading={stoneLoader}
                        rowSelectionModel={selectionModel}
                        getRowClassName={(params) =>
                            `super-app-theme--${params.row.status}`
                        }
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    locationid: false,
                                    kapanid: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500,
                                },
                            },
                        }}
                    />
                </Box>
            </Container >
    )
}  